import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  Filter,
  ReceiptText,
  Search,
  Undo2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button as AriaButton, Disclosure, DisclosurePanel, Heading } from 'react-aria-components';
import type { ReceiptSort, SalesSort } from '../../api/endpoints';
import { receiptsListQuery } from '../../api/queries';
import type { ReceiptSummary } from '../../api/types';
import { useAccess } from '../../app/access';
import { useI18n } from '../../i18n/I18nProvider';
import { Button } from '../../ui/Button';
import { Checkbox, CheckboxGroup } from '../../ui/Choice';
import { EmptyState } from '../../ui/Feedback';
import { Money, Page, useDocumentTitle } from '../../ui/Layout';
import { QueryError } from '../../ui/QueryError';
import { SelectField } from '../../ui/Select';
import { DataTable, Pagination, type Column } from '../../ui/Table';
import { LIVE_STATES } from '../sales/status';
import styles from './Receipt.module.css';
import {
  RECEIPT_ETA_STATUSES,
  RECEIPT_PAGE_SIZES,
  RECEIPT_STATUSES,
  RECEIPT_TYPES,
  receiptEtaAxis,
} from './receipts';
import { ReceiptTwoAxisCell, useReceiptTypeLabel } from './ReceiptStatus';

const PAGE_SIZES = [...RECEIPT_PAGE_SIZES];
/**
 * The URL uses the same sort vocabulary as the billing register; the receipts API names the buyer
 * sort `buyer`, so `customer` is translated when the query is built.
 */
const SORT_KEYS: SalesSort[] = ['number', 'date', 'customer', 'amount', 'status', 'updated'];

export interface ReceiptListSearch {
  q?: string;
  status?: string;
  etaStatus?: string;
  type?: string;
  sort?: SalesSort;
  dir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/** Reads the URL search back, the same defensive way the billing register does. */
export function parseReceiptListSearch(search: Record<string, unknown>): ReceiptListSearch {
  const str = (k: string) => {
    const v = search[k];
    if (typeof v === 'string') return v || undefined;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    return undefined;
  };
  const num = (k: string) => {
    const n = Number(search[k]);
    return Number.isInteger(n) && n > 0 ? n : undefined;
  };
  const out: ReceiptListSearch = {};
  const sort = str('sort');
  const dir = str('dir');
  if (sort && (SORT_KEYS as string[]).includes(sort)) out.sort = sort as SalesSort;
  if (dir === 'asc' || dir === 'desc') out.dir = dir;
  for (const k of ['q', 'status', 'etaStatus', 'type'] as const) {
    const v = str(k);
    if (v) out[k] = v;
  }
  const page = num('page');
  const limit = num('limit');
  if (page) out.page = page;
  if (limit && PAGE_SIZES.includes(limit)) out.limit = limit;
  return out;
}

/**
 * MF-RCP-001, the e-receipts register: search, the two status axes as filters, sortable columns,
 * server pagination, and the two-axis status (receipt stage + ETA status) shown together. Empty,
 * loading and error states are explicit. The routes are the product's own.
 */
export function ReceiptListPage() {
  const { t } = useI18n();
  const { orgId = '' } = useParams({ strict: false });
  const search = useSearch({ strict: false }) as ReceiptListSearch;
  const navigate = useNavigate();
  const access = useAccess(orgId);
  useDocumentTitle(t.receipts.title);

  const setSearch = (patch: Partial<ReceiptListSearch>, resetPage = true) =>
    void navigate({
      to: '/orgs/$orgId/sales/receipts',
      params: { orgId },
      search: (prev: ReceiptListSearch) => {
        const merged: Record<string, unknown> = { ...prev, ...patch };
        if (resetPage && !('page' in patch)) merged.page = undefined;
        return Object.fromEntries(
          Object.entries(merged).filter(([, v]) => v !== undefined && v !== ''),
        ) as ReceiptListSearch;
      },
      replace: true,
    });

  const page = search.page ?? 1;
  const limit = search.limit ?? 25;
  const sort: SalesSort = search.sort ?? 'updated';
  const dir = search.dir ?? 'desc';
  const apiSort: ReceiptSort = sort === 'customer' ? 'buyer' : sort;
  const list = useQuery({
    ...receiptsListQuery(orgId, {
      page,
      limit,
      sort: apiSort,
      dir,
      ...(search.q ? { q: search.q } : {}),
      ...(search.status ? { status: search.status } : {}),
      ...(search.etaStatus ? { etaStatus: search.etaStatus } : {}),
      ...(search.type ? { receiptType: search.type } : {}),
    }),
    placeholderData: keepPreviousData,
    refetchInterval: (q) =>
      q.state.data?.items.some((r) => r.etaStatus && LIVE_STATES.has(r.etaStatus)) ? 1500 : false,
  });
  const rows = list.data?.items ?? [];
  const filterCount = [search.status, search.etaStatus, search.type].filter(Boolean).length;
  const hasCriteria = filterCount > 0 || Boolean(search.q);

  const columns = useMemo<Column<ReceiptSummary>[]>(() => {
    const sortHeader = (key: SalesSort, label: string) => {
      const active = sort === key;
      const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
      return (
        <button
          type="button"
          className={styles.sortButton}
          aria-label={t.receipts.sortBy(label)}
          data-testid={`sort-${key}`}
          data-sort={active ? dir : undefined}
          onClick={() =>
            setSearch({
              sort: key,
              dir: active && dir === 'asc' ? 'desc' : active ? 'asc' : 'desc',
            })
          }
        >
          {label}
          <Icon size={13} aria-hidden="true" />
        </button>
      );
    };
    const sorted = (key: SalesSort) =>
      sort === key ? (dir === 'asc' ? ('ascending' as const) : ('descending' as const)) : undefined;
    return [
      {
        key: 'status',
        header: sortHeader('status', t.receipts.columns.status),
        label: t.receipts.columns.status,
        sort: sorted('status'),
        isPrimary: true,
        phoneOrder: 1,
        cell: (r) => (
          <ReceiptTwoAxisCell
            status={r.status}
            etaStatus={receiptEtaAxis(r)}
            environment={r.etaEnvironment}
            notAccepted={r.etaNotAccepted}
          />
        ),
      },
      {
        // The receipt: its number, and its kind as a second line (WP-8.9, role 4's L-2). The kind
        // qualifies the document; it is not a column of its own.
        key: 'number',
        header: sortHeader('number', t.receipts.columns.number),
        label: t.receipts.columns.number,
        sort: sorted('number'),
        phoneOrder: 2,
        cell: (r) => (
          <span className={styles.receiptCell}>
            {r.receiptNumber ? (
              <Link
                to="/orgs/$orgId/sales/receipts/$receiptId"
                params={{ orgId, receiptId: r.id }}
                className={styles.numberLink}
                data-testid="row-number"
              >
                {r.receiptNumber}
              </Link>
            ) : (
              '—'
            )}
            <ReceiptTypeLine code={r.receiptType} />
          </span>
        ),
      },
      {
        key: 'buyer',
        header: sortHeader('customer', t.receipts.columns.buyer),
        label: t.receipts.columns.buyer,
        sort: sorted('customer'),
        phoneOrder: 3,
        // A draft without a buyer name has none yet; an issued receipt without one was issued to an
        // unidentified buyer (RB3R8-17), the invoice list's word for the same case.
        cell: (r) => (
          <span data-testid="row-buyer">
            {r.buyerName ?? (
              <span className={styles.subtle}>
                {r.status === 'draft' ? t.receipts.noBuyer : t.receipts.anonymousBuyer}
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'amount',
        header: sortHeader('amount', t.receipts.columns.amount),
        label: t.receipts.columns.amount,
        sort: sorted('amount'),
        align: 'end',
        phoneOrder: 4,
        // In the receipt's own currency: Block 8 stores receipt amounts in it (WP-8.9b). A draft has
        // no total yet (the server calculates it at issue; the stored 0 is a placeholder), so it
        // shows the dash an invoice draft without a total shows.
        cell: (r) => (
          <Money
            value={r.status === 'draft' ? null : r.totalAmount}
            currency={r.currency}
            testId="row-amount"
          />
        ),
      },
      {
        key: 'date',
        header: sortHeader('date', t.receipts.columns.date),
        label: t.receipts.columns.date,
        sort: sorted('date'),
        phoneOrder: 5,
        cell: (r) => <DateCell value={r.date} />,
      },
      {
        key: 'updated',
        header: sortHeader('updated', t.receipts.columns.updated),
        label: t.receipts.columns.updated,
        sort: sorted('updated'),
        phoneOrder: 6,
        cell: (r) => <DateTimeCell value={r.lastActivityAt} />,
      },
    ];
    // setSearch reads the latest URL through navigate(); it is intentionally out of the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, orgId, sort, dir]);

  const live = rows.some((r) => r.etaStatus && LIVE_STATES.has(r.etaStatus));
  const canCreate = access.can('receipt.create');
  const canReturn = access.can('receipt.return') || canCreate;
  const isEmptyOrg = Boolean(list.data) && (list.data?.total ?? 0) === 0 && !hasCriteria;

  return (
    <Page>
      <section className={styles.hero}>
        <div className={styles.heroRow}>
          <div className={styles.heroText}>
            <span className={styles.heroEyebrow}>
              <ReceiptText size={14} aria-hidden="true" />
              {t.receipts.eyebrow}
            </span>
            <h1 className={styles.heroTitle}>{t.receipts.title}</h1>
          </div>
          <div className={styles.heroActions}>
            {canReturn ? (
              <Button
                variant="secondary"
                icon={<Undo2 size={16} aria-hidden="true" />}
                testId="new-return"
                onPress={() =>
                  void navigate({
                    to: '/orgs/$orgId/sales/receipts/new',
                    params: { orgId },
                    search: { type: 'RWR' },
                  })
                }
              >
                {t.receipts.newReturn}
              </Button>
            ) : null}
            {canCreate ? (
              <Button
                variant="primary"
                icon={<ReceiptText size={16} aria-hidden="true" />}
                testId="new-receipt"
                onPress={() =>
                  void navigate({ to: '/orgs/$orgId/sales/receipts/new', params: { orgId } })
                }
              >
                {t.receipts.newReceipt}
              </Button>
            ) : null}
          </div>
        </div>
        {live ? (
          <span className={styles.live} aria-live="polite">
            <span className={styles.dot} />
            {t.receipts.updating}
          </span>
        ) : null}
      </section>

      {isEmptyOrg ? null : <FindPanel search={search} onChange={setSearch} count={filterCount} />}

      {list.isError ? (
        <QueryError error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <>
          <DataTable
            caption={t.receipts.title}
            testId="receipts-table"
            columns={columns}
            rows={rows}
            getRowKey={(r) => r.id}
            getRowTestId={(r) => `row-${r.receiptNumber ?? r.id}`}
            isLoading={list.isLoading}
            empty={
              hasCriteria ? (
                <EmptyState icon={<Search size={22} />} title={t.receipts.noResultsTitle}>
                  {t.receipts.noResultsBody}
                </EmptyState>
              ) : (
                <EmptyState
                  icon={<ReceiptText size={22} />}
                  title={t.receipts.emptyTitle}
                  action={
                    canCreate ? (
                      <Button
                        variant="primary"
                        testId="empty-new-receipt"
                        onPress={() =>
                          void navigate({
                            to: '/orgs/$orgId/sales/receipts/new',
                            params: { orgId },
                          })
                        }
                      >
                        {t.receipts.newReceipt}
                      </Button>
                    ) : undefined
                  }
                >
                  {t.receipts.emptyBody}
                </EmptyState>
              )
            }
            footer={
              // No pager under an empty result (RB3R8-27).
              (list.data?.total ?? 0) === 0 ? undefined : (
                <Pagination
                  page={page}
                  limit={limit}
                  total={list.data?.total ?? 0}
                  pageSizes={PAGE_SIZES}
                  showEnds
                  onPageChange={(next) => setSearch({ page: next }, false)}
                  onLimitChange={(next) => setSearch({ limit: next, page: undefined }, false)}
                />
              )
            }
          />
        </>
      )}
    </Page>
  );
}

/** The receipt's kind, under its number. */
function ReceiptTypeLine({ code }: { code: string }) {
  const label = useReceiptTypeLabel();
  return (
    <span className={styles.typeLine} data-testid="row-type" data-type={code}>
      {label(code)}
    </span>
  );
}

function DateCell({ value }: { value: string | null }) {
  const { formatDate } = useI18n();
  return value ? (
    <span data-value={value}>{formatDate(value)}</span>
  ) : (
    <span className={styles.subtle}>—</span>
  );
}

function DateTimeCell({ value }: { value: string }) {
  const { formatDateTime } = useI18n();
  return <span className={styles.updatedCell}>{formatDateTime(value)}</span>;
}

/** The find panel: search (debounced), the two status axes, the type, and a clear-all. */
function FindPanel({
  search,
  onChange,
  count,
}: {
  search: ReceiptListSearch;
  onChange: (patch: Partial<ReceiptListSearch>) => void;
  count: number;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(count > 0);
  const [text, setText] = useState(search.q ?? '');
  useEffect(() => setText(search.q ?? ''), [search.q]);
  useEffect(() => {
    if (text === (search.q ?? '')) return;
    const timer = setTimeout(() => onChange({ q: text || undefined }), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const statuses = (search.status ?? '').split(',').filter(Boolean);
  const etaStatuses = (search.etaStatus ?? '').split(',').filter(Boolean);
  const ordered = (all: readonly string[], picked: string[]) =>
    all.filter((v) => picked.includes(v)).join(',') || undefined;

  return (
    <aside className={styles.findPanel} aria-label={t.receipts.findLabel}>
      <div className={styles.findSearch}>
        <label className={styles.findHeading} htmlFor="receipts-search">
          {t.receipts.search}
        </label>
        <div className={styles.search}>
          <Search size={16} aria-hidden="true" />
          <input
            id="receipts-search"
            type="search"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="list-search"
          />
        </div>
        <p className={styles.findHint}>{t.receipts.searchHint}</p>
      </div>

      <Disclosure
        isExpanded={expanded}
        onExpandedChange={setExpanded}
        className={styles.facetsDisclosure}
      >
        <Heading level={2} className={styles.findHeading}>
          <AriaButton slot="trigger" className={styles.facetsTrigger} data-testid="filter-open">
            <ChevronDown size={16} aria-hidden="true" className={styles.facetsChevron} />
            <Filter size={15} aria-hidden="true" />
            {count ? t.receipts.filterCount(count) : t.receipts.filter}
          </AriaButton>
        </Heading>
        <DisclosurePanel>
          <div className={styles.facets} data-testid="filter-panel">
            <CheckboxGroup
              label={t.receipts.filterStatus}
              value={statuses}
              onChange={(next) => onChange({ status: ordered(RECEIPT_STATUSES, next) })}
              listClassName={styles.facetChecks}
            >
              {RECEIPT_STATUSES.map((s) => (
                <Checkbox key={s} value={s} fieldName={`filter-status-${s}`}>
                  {t.docStatus[s]}
                </Checkbox>
              ))}
            </CheckboxGroup>
            <CheckboxGroup
              label={t.receipts.filterTaxStatus}
              value={etaStatuses}
              onChange={(next) => onChange({ etaStatus: ordered(RECEIPT_ETA_STATUSES, next) })}
              listClassName={styles.facetChecks}
            >
              {RECEIPT_ETA_STATUSES.map((s) => (
                <Checkbox key={s} value={s} fieldName={`filter-tax-${s}`}>
                  {s === 'not_accepted' ? t.receipts.notAccepted : t.taxStatus[s]}
                </Checkbox>
              ))}
            </CheckboxGroup>
            <SelectField
              label={t.receipts.filterType}
              fieldName="filter-type"
              options={[
                { id: 'all', label: t.receipts.filterAllTypes },
                ...RECEIPT_TYPES.map((type) => ({
                  id: type.code,
                  label:
                    (t.receipts.types as Record<string, string | undefined>)[type.code] ??
                    type.code,
                })),
              ]}
              value={search.type ?? 'all'}
              onChange={(value) =>
                onChange({ type: !value || value === 'all' ? undefined : value })
              }
            />
            <Button
              variant="ghost"
              size="sm"
              testId="clear-filters"
              className={styles.clearFacets}
              onPress={() => {
                setText('');
                onChange({
                  q: undefined,
                  status: undefined,
                  etaStatus: undefined,
                  type: undefined,
                });
              }}
            >
              {t.receipts.clearFilter}
            </Button>
          </div>
        </DisclosurePanel>
      </Disclosure>
    </aside>
  );
}
